import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Reservation } from '@turizi/shared';
import { formatDayLong, formatDayShort, setWebTitle } from '@/lib/format-utils';
import { RESERVATION_STATUS } from '@turizi/shared';
import ReservationPriceTotals from '@/components/reservation-price-totals';
import {
    IconCalendar,
} from '@tabler/icons-react';
import { useMemo } from 'react';
import PaxGroupList from '@/components/pax-groups/pax-group-list';
import ServiceBookingsList from '@/components/service-bookings-list';
import ShowMetadata from '@/components/show-metadata';
import { BUSINESSES_PATH } from '@/lib/navigation';
import { TagIcon } from '@/lib/tag-utils';

interface Props {
    reservation: Reservation;
}

export default function ReservationViewPage({ reservation }: Props) {
    const { t } = useTranslation();
    const { id } = useParams<{ id: string }>();

    console.log(reservation.seller, reservation.seller_id);

    const clientName = reservation?.client_business?.name;

    setWebTitle(`${t('reservation')} #${id} - ${clientName || '...'} - ${formatDayShort(reservation?.start_day)} - ${formatDayShort(reservation?.end_day)}`);

    if (!reservation) return null;

    const statusColor = RESERVATION_STATUS[reservation.status as keyof typeof RESERVATION_STATUS]?.color || '#64748b';

    return (
        <div className="panel-content space-y-6">
            {/* Header section */}
            <div className="flex items-center gap-3">
                <div className="font-bold text-lg text-slate-800 flex items-center gap-2">
                    <span className='font-light'>{t('reservation')}</span> #{reservation.id}
                </div>
                <div className="flex items-center gap-1.5 font-semibold text-slate-700">
                    <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: statusColor }} />
                    <span className="uppercase text-[11px] tracking-wider " title={t(`reservation_${reservation.status}`)}>
                        {t(`reservation_${reservation.status}`)}
                    </span>
                </div>
            </div>

            {/* Summary cards grid */}
            <div className="flex gap-4 items-start justify-between flex-wrap">

                {/* Client */}
                <div className="w-1/5 p-4 border border-slate-100 rounded-xl bg-white shadow-sm">
                    <Link to={`${BUSINESSES_PATH}/${reservation?.client_business?.id}`}>
                        <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1.5">
                            {t('client')}
                        </div>
                        <div className="flex items-center gap-3 p-3 bg-slate-50/60 rounded-xl border border-slate-100">
                            <TagIcon
                                name={reservation.client_business?.category}
                                hideText
                                noBackground
                                size='md'
                            />
                            <div className="min-w-0">
                                <div className="text-sm font-bold text-blue-500 truncate">
                                    {clientName}
                                </div>
                                <div className="text-[10px] text-gray-500 font-medium uppercase tracking-wider">
                                    {reservation?.client_business ? t(reservation.client_business.category) : t('pax')}
                                </div>
                            </div>
                        </div>
                    </Link>
                </div>

                {/* Dates */}
                <div className="p-4 border border-slate-100 rounded-xl bg-white flex items-center gap-3 shadow-sm hover:shadow-md transition-all duration-200">
                    <div className="p-2.5 rounded-lg bg-indigo-50 text-indigo-600 shrink-0 flex items-center justify-center">
                        <IconCalendar size={18} />
                    </div>
                    <div className="min-w-0 flex-1">
                        <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">
                            {t('dates')}
                        </div>
                        <div className="text-slate-700 font-semibold text-xs leading-tight">
                            {formatDayLong(reservation.start_day)} - {formatDayLong(reservation.end_day)}
                        </div>
                    </div>
                </div>

                {/* Totals */}
                <div className="flex-1 p-4 border border-slate-100 rounded-xl bg-white flex items-start gap-3 shadow-sm hover:shadow-md transition-all duration-200">
                    <div className="min-w-0 flex-1">
                        <div className="mt-0.5 flex justify-end">
                            <ReservationPriceTotals reservation={reservation} size="sm" />
                        </div>
                    </div>
                </div>


                {/* Secondary Metadata footer */}
                <div className="p-4 border border-slate-100 rounded-xl bg-white shadow-sm flex gap-4">
                    <div className="text-[11px]">
                        <span className="text-gray-400 font-semibold uppercase text-[9px] tracking-wider block">
                            {t('due_date_short')}
                        </span>
                        <span className="font-semibold text-slate-700">
                            {formatDayLong(reservation?.payment_due_day, '-- / --- / ----')}
                        </span>
                    </div>
                    <div className="text-[11px]">
                        <span className="text-gray-400 font-semibold uppercase text-[9px] tracking-wider block">
                            {t('receipt_number')}
                        </span>
                        <span className="font-semibold text-slate-700">
                            {[
                                ...(reservation?.afip_vouchers?.map(v => `${String(v.pos).padStart(4, '0')}-${String(v.voucher_number).padStart(8, '0')}`) || []),
                                ...(reservation?.tax_id ? [reservation.tax_id] : [])
                            ].join(', ') || '--'}
                        </span>
                    </div>
                </div>
            </div>

            {/* Split layout (Pax List & Itinerary) */}
            <div className="flex flex-col lg:flex-row gap-6 items-start">
                {/* Pax List side card */}
                <div className="space-y-2 w-1/5 p-4">
                    <PaxGroupList
                        reservationId={Number(reservation.id)}
                        bookings={reservation.service_bookings || []}
                        editMode={false}
                    />
                    {/* Notes Section */}
                    {reservation?.observations && (
                        <div className="solid-box-border-negative">
                            <div className="flex-1">
                                <div className="text-xs text-gray-700 font-medium whitespace-pre-wrap">
                                    {reservation.observations}
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Itinerary Table side */}
                <div className="flex-1 w-full min-w-0">
                    <ServiceBookingsList
                        bookings={reservation.service_bookings}
                        showReservation={false}
                        showServiceOrder={true}
                        editable={false}
                        showPaymentStatus={true}
                        showCost={true}
                        showPrice={true}
                        showProvider={true}
                        showPhone={true}
                    />
                </div>
            </div>

            <ShowMetadata item={reservation} />
        </div>
    );
}
